import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Shortcut {
  id: string;
  label: string;
  icon: string;
  routerLink?: string[];
  permission?: string;
  href?: string;
  backgroundColor?: string;
  textColor?: string;
  alwaysShow?: boolean;
  order?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ShortcutService {
  private readonly STORAGE_KEY = 'user_shortcuts';
  private shortcutsSubject = new BehaviorSubject<Shortcut[]>([]);
  shortcuts$ = this.shortcutsSubject.asObservable();

  // Default shortcuts matching the accueil page
  private defaultShortcuts: Shortcut[] = [
    { id: 'dashboard', label: 'Dashboard Informatique', icon: 'pi pi-fw pi-desktop', href: 'https://dashboard.hamel.construction/', textColor: 'oklch(72.3% .219 149.579)', alwaysShow: true },
    { id: 'emplacements', label: 'Emplacements', icon: 'pi pi-fw pi-map', routerLink: ['/emplacements'], permission: 'emplacements' },
    { id: 'accueil', label: 'Accueil', icon: 'pi pi-fw pi-home', routerLink: ['/'], permission: 'accueil' },
    { id: 'items', label: 'Items', icon: 'pi pi-fw pi-id-card', routerLink: ['/items'], permission: 'items' },
    { id: 'items_incomplets', label: 'Items incomplets', icon: 'pi pi-fw pi-id-card', routerLink: ['/items/incomplet'], permission: 'items_incomplets' },
    { id: 'items_archives', label: 'Items archivés', icon: 'pi pi-fw pi-id-card', routerLink: ['/items/archive'], permission: 'items_archives' },
    { id: 'reparations', label: 'Réparations', icon: 'pi pi-fw pi-wrench', routerLink: ['/items/reparations'], permission: 'reparations' },
    { id: 'garanties', label: 'Garanties', icon: 'pi pi-fw pi-shield', routerLink: ['/garanties'], permission: 'garanties' },
    { id: 'locations', label: 'Locations', icon: 'pi pi-fw pi-tag', routerLink: ['/locations'], permission: 'locations' },
    { id: 'boites_outils', label: 'Boites d\'outils', icon: 'pi pi-fw pi-cog', routerLink: ['/admin/boitesOutil'], permission: 'boites_outils_consulter' },
    { id: 'unites', label: 'Unités', icon: 'pi pi-fw pi-id-card', routerLink: ['/mecanique/unites'], permission: 'unites' },
    { id: 'pieces', label: 'Pièces', icon: 'pi pi-fw pi-id-card', routerLink: ['/mecanique/pieces'], permission: 'pieces' },
    { id: 'bonstravail', label: 'Bons de travail', icon: 'pi pi-fw pi-check-square', routerLink: ['/mecanique/bontravails'], permission: 'bonstravail' },
    { id: 'planification', label: 'Planification', icon: 'pi pi-fw pi-calendar', routerLink: ['/mecanique/planification'], permission: 'temp_mec' },
    { id: 'heures', label: 'Heures/Km', icon: 'pi pi-fw pi-clock', routerLink: ['/mecanique/heures'], permission: 'temp_mec' },
    { id: 'suiviheures', label: 'Suivi des heures', icon: 'pi pi-fw pi-chart-line', routerLink: ['/mecanique/suiviheureskms'], permission: 'temp_mec' },
    { id: 'precommandes', label: 'Commandes', icon: 'pi pi-fw pi-pencil', routerLink: ['/commandes'], permission: 'precommande' },
    { id: 'approbation_precommandes', label: 'Approbation commandes', icon: 'pi pi-fw pi-check-square', routerLink: ['/commandes/approbation'], permission: 'approbation_precommande' },
    { id: 'commandes', label: 'Admin Commandes', icon: 'pi pi-fw pi-book', routerLink: ['/commandes/admin'], permission: 'commandes' },
    { id: 'requisitions', label: 'Transports', icon: 'pi pi-fw pi-tablet', routerLink: ['/transports'], permission: 'requisitions' },
    { id: 'fournisseurs', label: 'Fournisseurs', icon: 'pi pi-user', routerLink: ['/fournisseurs'], permission: 'fournisseurs' },
    { id: 'bdcs', label: 'Bons de commande', icon: 'pi pi-shopping-cart', routerLink: ['/achats'], permission: 'bdcs' },
    { id: 'ventes', label: 'Ventes', icon: 'pi pi-shopping-bag', routerLink: ['/ventes'], permission: 'ventes' },
    { id: 'liste_achat', label: 'Compléter une liste', icon: 'pi pi-fw pi-pencil', routerLink: ['/listeachat'], permission: 'liste_achat' },
    { id: 'approbation_liste_achat', label: 'Approuver une liste', icon: 'pi pi-fw pi-check-square', routerLink: ['/approbationlisteachat'], permission: 'approuver_liste_achat' },
    { id: 'non_conformites', label: 'Non conformités', icon: 'pi pi-fw pi-exclamation-triangle', routerLink: ['/assurancequalite/nonconformite'], permission: 'assurance_qualite' },
    { id: 'actions_correctives', label: 'Actions correctives', icon: 'pi pi-fw pi-file-check', routerLink: ['/assurancequalite/actioncorrective'], permission: 'assurance_qualite' },
    { id: 'rapport_journalier_sst', label: 'Rapport journalier SST', icon: 'pi pi-fw pi-chart-bar', routerLink: ['/assurancequalite/rapportjournalier'], permission: 'assurance_qualite' },
    { id: 'emprunts', label: 'Emprunts', icon: 'pi pi-fw pi-id-card', routerLink: ['/emprunt'], permission: 'emprunt' }
  ];

  constructor() {
    this.loadShortcuts();
  }

  private loadShortcuts() {
    // Get always show shortcuts
    const alwaysShowShortcuts = this.defaultShortcuts.filter(s => s.alwaysShow);
    
    const savedShortcuts = localStorage.getItem(this.STORAGE_KEY);
    if (savedShortcuts) {
      // Filter out shortcuts that are no longer in defaultShortcuts
      const parsedShortcuts = JSON.parse(savedShortcuts);
      
      // Update existing shortcuts with any changes from defaultShortcuts
      const updatedShortcuts = parsedShortcuts.map((shortcut: Shortcut) => {
        const defaultShortcut = this.defaultShortcuts.find(ds => ds.id === shortcut.id);
        return defaultShortcut ? { ...defaultShortcut, order: shortcut.order } : shortcut;
      }).filter((shortcut: Shortcut) => {
        // Validate shortcut structure and exclude alwaysShow shortcuts
        const hasValidNavigation = (shortcut.routerLink && !shortcut.href) || (!shortcut.routerLink && shortcut.href);
        return this.defaultShortcuts.some(ds => ds.id === shortcut.id) && 
               hasValidNavigation && 
               !shortcut.alwaysShow;
      });
      
      // Sort by order
      updatedShortcuts.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // Save filtered and updated shortcuts (excluding alwaysShow)
      if (JSON.stringify(updatedShortcuts) !== savedShortcuts) {
        this.saveToStorage(updatedShortcuts);
      }
      
      // Combine always show shortcuts with saved shortcuts
      this.shortcutsSubject.next([...alwaysShowShortcuts, ...updatedShortcuts]);
    } else {
      // Initialize with default shortcuts if none exist
      const nonAlwaysShowDefaults = this.defaultShortcuts.filter(s => !s.alwaysShow);
      this.shortcutsSubject.next([...alwaysShowShortcuts, ...nonAlwaysShowDefaults]);
      this.saveToStorage(nonAlwaysShowDefaults);
    }
  }

  private saveToStorage(shortcuts: Shortcut[]) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(shortcuts));
  }

  addShortcut(shortcut: Shortcut) {
    // Validate that shortcut doesn't have both routerLink and href
    if (shortcut.routerLink && shortcut.href) {
      throw new Error('A shortcut cannot have both routerLink and href defined');
    }
    
    // Validate that shortcut has at least one of routerLink or href
    if (!shortcut.routerLink && !shortcut.href) {
      throw new Error('A shortcut must have either routerLink or href defined');
    }

    const currentShortcuts = this.shortcutsSubject.value;
    if (!currentShortcuts.find(s => s.id === shortcut.id)) {
      // Set order to be last among non-alwaysShow shortcuts
      const maxOrder = Math.max(...currentShortcuts.filter(s => !s.alwaysShow).map(s => s.order || 0), -1);
      const shortcutWithOrder = { ...shortcut, order: maxOrder + 1 };
      
      const newShortcuts = [...currentShortcuts, shortcutWithOrder];
      this.shortcutsSubject.next(newShortcuts);
      this.saveToStorage(newShortcuts.filter(s => !s.alwaysShow));
    }
  }

  removeShortcut(shortcutId: string) {
    const currentShortcuts = this.shortcutsSubject.value;
    const newShortcuts = currentShortcuts.filter(s => s.id !== shortcutId);
    this.shortcutsSubject.next(newShortcuts);
    this.saveToStorage(newShortcuts.filter(s => !s.alwaysShow));
  }

  resetToDefault() {
    this.shortcutsSubject.next([...this.defaultShortcuts]);
    this.saveToStorage(this.defaultShortcuts.filter(s => !s.alwaysShow));
  }

  getAvailableShortcuts(): Shortcut[] {
    return this.defaultShortcuts;
  }

  getCurrentShortcuts(): Shortcut[] {
    return this.shortcutsSubject.value;
  }

  reorderShortcuts(shortcuts: Shortcut[]) {
    // Update order property for each shortcut
    const reorderedShortcuts = shortcuts.map((shortcut, index) => ({
      ...shortcut,
      order: index
    }));
    
    // Separate always show shortcuts from reorderable ones
    const alwaysShowShortcuts = reorderedShortcuts.filter(s => s.alwaysShow);
    const reorderableShortcuts = reorderedShortcuts.filter(s => !s.alwaysShow);
    
    // Update the subject with new order
    this.shortcutsSubject.next([...alwaysShowShortcuts, ...reorderableShortcuts]);
    
    // Save only the reorderable shortcuts to storage
    this.saveToStorage(reorderableShortcuts);
  }
} 